import React from 'react';
import ShiftCard from './ShiftCard';
import type { Shift } from '../../dtos/scheduling/schedule.dto';
import { isCarryOver, longDateLabel, shiftsOnDay } from './scheduleUtils';

export interface AgendaListProps {
  days: string[];
  shifts: Shift[];
  today: string;
  primary?: 'patient' | 'provider';
  selectedIds?: Set<string>;
  onToggleSelect?: (shift: Shift) => void;
  onOpen?: (shift: Shift) => void;
  emptyText?: string;
  /** Hide days without shifts (default true) */
  compactEmpty?: boolean;
}

/**
 * Day-grouped list: the phone layout for every calendar view (plan §6.3), and the patient
 * chart's Schedule tab. An overnight shift also appears on the day it ends.
 */
const AgendaList: React.FC<AgendaListProps> = ({ days, shifts, today, primary = 'patient', selectedIds, onToggleSelect, onOpen, emptyText = 'No shifts in this range.', compactEmpty = true }) => {
  const groups = days.map((d) => ({ date: d, items: shiftsOnDay(shifts, d) })).filter((g) => !compactEmpty || g.items.length);
  if (!groups.length) return <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">{emptyText}</div>;
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <section key={g.date} aria-label={longDateLabel(g.date)}>
          <h3 className={`sticky top-0 z-10 bg-gray-50/95 py-1 text-xs font-semibold uppercase tracking-wide ${g.date === today ? 'text-sky-700' : 'text-gray-500'}`}>
            {g.date === today ? 'Today · ' : ''}{longDateLabel(g.date)}
          </h3>
          <div className="mt-1 space-y-2">
            {g.items.length === 0 && <p className="text-xs text-gray-400">No shifts</p>}
            {g.items.map((s) => (
              <ShiftCard
                key={`${s.id}-${g.date}`}
                shift={s}
                primary={primary}
                carryOver={isCarryOver(s, g.date)}
                selected={selectedIds?.has(s.id)}
                onSelect={onToggleSelect}
                onOpen={onOpen}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default AgendaList;
